import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Globe2, ShieldQuestion, Save } from "lucide-react";

const OTHER_LANGS = ["fr", "de", "it", "en"];
const CANTONS = ["", "VD", "GE", "VS", "FR", "NE", "JU", "BE", "ZH", "LU", "SG", "TI", "BS", "BL", "AG", "GR", "SO", "TG", "SZ", "ZG"];
const PRIV = ["show_city", "show_artists", "show_events", "show_attendance", "show_languages"];

export default function CommunityPrefs() {
  const { t } = useLang();
  const [p, setP] = useState(null);

  useEffect(() => { api.get("/community/preferences").then((r) => setP(r.data)).catch(() => {}); }, []);
  if (!p) return null;

  const save = async () => {
    try { await api.put("/community/preferences", p); toast.success(t("com_saved")); } catch { toast.error(t("com_error")); }
  };
  const toggleUnderstood = (l) => setP({ ...p, understood_languages: p.understood_languages?.includes(l) ? p.understood_languages.filter((x) => x !== l) : [...(p.understood_languages || []), l] });
  const setPriv = (k, v) => setP({ ...p, privacy: { ...(p.privacy || {}), [k]: v } });
  const field = "bg-[#0B0C0E] border border-[#272A34] rounded-md px-3 py-2 text-sm text-stone-200";

  return (
    <section className="mb-14" data-testid="community-prefs">
      <h2 className="font-serif text-2xl text-stone-100 mb-5 flex items-center gap-2 border-b border-amber-900/30 pb-3"><Globe2 className="w-5 h-5 text-amber-500" /> {t("com_prefs_title")}</h2>
      <div className="bg-[#121418] border border-[#272A34] rounded-lg p-5 space-y-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-stone-500 mb-1.5">{t("com_prefs_primary")}</label>
            <select data-testid="prefs-primary" value={p.primary_language || "fr"} onChange={(e) => setP({ ...p, primary_language: e.target.value })} className={`${field} w-full`}>
              <option value="fr">Français</option><option value="de">Deutsch</option><option value="it">Italiano</option><option value="en">English</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-stone-500 mb-1.5">{t("com_prefs_canton")}</label>
            <select data-testid="prefs-canton" value={p.canton || ""} onChange={(e) => setP({ ...p, canton: e.target.value })} className={`${field} w-full`}>
              {CANTONS.map((c) => <option key={c} value={c}>{c || "—"}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="block text-xs font-mono uppercase tracking-wider text-stone-500 mb-1.5">{t("com_prefs_understood")}</label>
          <div className="flex flex-wrap gap-2">
            {OTHER_LANGS.map((l) => (
              <button key={l} data-testid={`prefs-lang-${l}`} onClick={() => toggleUnderstood(l)} className={`px-3 py-1.5 rounded-full text-xs uppercase font-mono ${p.understood_languages?.includes(l) ? "bg-amber-700 text-stone-950" : "bg-[#0B0C0E] text-stone-300 border border-[#272A34]"}`}>{l}</button>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-xs font-mono uppercase tracking-wider text-stone-500 mb-2 flex items-center gap-1.5"><ShieldQuestion className="w-4 h-4" /> {t("com_prefs_privacy")}</label>
          <div className="mb-3">
            <select data-testid="prefs-visibility" value={p.privacy?.profile || "members"} onChange={(e) => setPriv("profile", e.target.value)} className={`${field}`}>
              <option value="public">{t("com_prefs_public")}</option><option value="members">{t("com_prefs_members")}</option><option value="private">{t("com_prefs_private")}</option>
            </select>
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            {PRIV.map((k) => (
              <label key={k} className="flex items-center gap-2.5 text-sm text-stone-300 cursor-pointer">
                <input type="checkbox" data-testid={`prefs-${k}`} checked={p.privacy?.[k] !== false} onChange={(e) => setPriv(k, e.target.checked)} className="w-4 h-4 accent-amber-500" />
                {t(`com_prefs_${k}`)}
              </label>
            ))}
          </div>
        </div>
        <button data-testid="prefs-save" onClick={save} className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-500 text-stone-950 font-semibold px-5 py-2.5 rounded-md text-sm"><Save className="w-4 h-4" /> {t("com_prefs_save")}</button>
      </div>
    </section>
  );
}
